import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { SupportContact } from '@/shared/platform/SupportContact';
import {
  Anchor,
  Box,
  Button,
  Group,
  Paper,
  PasswordInput,
  Progress,
  SimpleGrid,
  Stack,
  Text,
  ThemeIcon,
  TextInput,
  Title,
} from '@mantine/core';
import { useForm, schemaResolver } from '@mantine/form';
import { z } from 'zod';
import { notifications } from '@mantine/notifications';
import { useState } from 'react';
import { IconLock, IconUserPlus } from '@tabler/icons-react';
import * as authApi from '@/features/auth/api';
import { ensureFingerprint } from '@/shared/lib/fingerprint';
import { env } from '@/shared/lib/env';
import { passwordSchema, computeStrength } from '@/features/auth/password-utils';
import { mw, mwSectionLabel, MW_AUTH_CARD_SHADOW } from '@/shared/mobile/tokens';
import { JsonApiHttpError } from '@/shared/api/jsonapi/errors';
import { userFacingMessage } from '@/shared/api/errorMessage';

const registerSchema = z.object({
  firstName: z.string().min(2, 'Min 2 caratteri'),
  lastName: z.string().min(2, 'Min 2 caratteri'),
  email: z.string().email('Email non valida'),
  password: passwordSchema,
});

type RegisterForm = z.infer<typeof registerSchema>;

export const Route = createFileRoute('/_auth/register')({ component: RegisterPage });

function RegisterPage() {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const form = useForm<RegisterForm>({
    mode: 'controlled',
    initialValues: { firstName: '', lastName: '', email: '', password: '' },
    validate: schemaResolver(registerSchema, { sync: true }),
  });
  const strength = computeStrength(form.values.password);

  const onSubmit = form.onSubmit(async (values) => {
    setBusy(true);
    try {
      const fingerprint = await ensureFingerprint();
      await authApi.register({
        Email: values.email,
        Password: values.password,
        FirstName: values.firstName,
        LastName: values.lastName,
        FingerPrint: fingerprint,
        UserLang: env.VITE_DEFAULT_LANG,
      });
      notifications.show({ color: 'green', message: 'Registrato. Controlla la tua email per il codice OTP.' });
      void navigate({ to: '/register-otp', search: { email: values.email } });
    } catch (err) {
      /*
       * ⚠️ Il 409 è **la** risposta da riconoscere qui: email già registrata. Fino al BE del 20 ago
       * 2026 quel caso rispondeva **500** — `HttpContext.Connection.RemoteIpAddress` è nullable e
       * veniva dereferenziato — quindi arrivava all'utente come errore interno, che è la cosa meno
       * utile da dirgli: chi ha già un account deve sapere di dover fare login, non di riprovare.
       * Il corpo del 409 è **vuoto**, quindi il codice non c'è: lo status è tutto ciò che abbiamo.
       */
      if (err instanceof JsonApiHttpError && err.status === 409) {
        notifications.show({
          color: 'red',
          title: 'Esiste già un account con questa email',
          message: 'Accedi con le tue credenziali, oppure usa «Password dimenticata» dalla pagina di login.',
        });
        form.setFieldError('email', 'Email già registrata.');
        return;
      }
      const msg = userFacingMessage(err, 'Errore');
      notifications.show({ color: 'red', title: 'Registrazione fallita', message: msg });
    } finally {
      setBusy(false);
    }
  });

  return (
    <Stack gap="xl">
      <Paper
        p={34}
        radius={22}
        style={{ border: `1px solid ${mw.line}`, boxShadow: MW_AUTH_CARD_SHADOW }}
      >
        <Stack>
          <Group gap="sm" wrap="nowrap" align="center">
            <ThemeIcon visibleFrom="md" size={44} radius="md" variant="filled" color="brand">
              <IconUserPlus size={24} />
            </ThemeIcon>
            <div>
              <Title order={3}>Crea il tuo account</Title>
              <Text c="dimmed" size="sm">
                Già registrato?{' '}
                <Anchor component={Link} to="/login" fw={600} c={mw.accentText}>Accedi</Anchor>
              </Text>
            </div>
          </Group>

          <form onSubmit={onSubmit}>
            <Stack>
              <SimpleGrid cols={{ base: 1, xs: 2 }} spacing="sm">
                <TextInput label="Nome" required {...form.getInputProps('firstName')} autoComplete="given-name" />
                <TextInput label="Cognome" required {...form.getInputProps('lastName')} autoComplete="family-name" />
              </SimpleGrid>
              <TextInput label="Email" required type="email" placeholder="tu@azienda.it" autoComplete="email" {...form.getInputProps('email')} />
              <PasswordInput
                label="Password"
                required
                autoComplete="new-password"
                placeholder="••••••••"
                leftSection={<IconLock size={16} />}
                {...form.getInputProps('password')}
              />
              {form.values.password.length > 0 && (
                <div>
                  <Group justify="space-between" mb={6}>
                    <Text style={mwSectionLabel}>Robustezza</Text>
                    <Text size="xs" fw={600} c={strength.color}>{strength.label}</Text>
                  </Group>
                  <Progress value={(strength.score / 4) * 100} color={strength.color} size="sm" radius="xl" />
                </div>
              )}
              <Button type="submit" loading={busy} fullWidth size="md">
                Crea account
              </Button>
              <Text size="xs" c="dimmed" ta="center" lh={1.5}>
                Ti invieremo un codice di verifica via email per confermare l'account.
              </Text>
            </Stack>
          </form>
        </Stack>
      </Paper>

      <Box ta="center">
        <Text size="xs" c="dimmed">
          Hai bisogno di aiuto?{' '}
          <SupportContact color={mw.accentText} />
        </Text>
      </Box>
    </Stack>
  );
}
